import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { motion, useReducedMotion } from 'motion/react';
import { getSettings } from '../settings';
import Folder3DScene from './Folder3DScene';

const scopes = [
  { id: 'my', label: 'My Dex' },
  { id: 'saved', label: 'My Saved Tags' },
  { id: 'shared', label: 'Shared Tags' },
  { id: 'digipals', label: 'My Digipals' }
];

const CLOSE_MS = 300;
const PAUSE_MS = 450;
const OPEN_MS = 420;

function RolodexFrame({ scope, direction = 'next', onScopeChange, children }) {
  const reduceMotion = useReducedMotion();
  const [settings, setSettings] = React.useState(getSettings);
  const [transitionPhase, setTransitionPhase] = React.useState('open');
  const [transitionDirection, setTransitionDirection] = React.useState(1);
  const timerRef = React.useRef(null);
  const activeIndex = Math.max(0, scopes.findIndex(item => item.id === scope));

  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);
  React.useEffect(() => {
    const handleSettingsChanged = event => setSettings(event.detail || getSettings());
    window.addEventListener('settings:changed', handleSettingsChanged);
    return () => window.removeEventListener('settings:changed', handleSettingsChanged);
  }, []);

  const navigate = React.useCallback((nextScope, movement) => {
    if (nextScope === scope || transitionPhase !== 'open') return;
    if (reduceMotion || settings.reduceAnimations) {
      onScopeChange(nextScope, movement);
      return;
    }

    setTransitionDirection(movement === 'next' ? 1 : -1);
    setTransitionPhase('closing');
    timerRef.current = window.setTimeout(() => {
      setTransitionPhase('side');
      onScopeChange(nextScope, movement);
      timerRef.current = window.setTimeout(() => {
        setTransitionPhase('opening');
        timerRef.current = window.setTimeout(() => setTransitionPhase('open'), OPEN_MS);
      }, PAUSE_MS);
    }, CLOSE_MS);
  }, [onScopeChange, reduceMotion, scope, settings.reduceAnimations, transitionPhase]);

  const previous = scopes[(activeIndex - 1 + scopes.length) % scopes.length];
  const next = scopes[(activeIndex + 1) % scopes.length];
  const upcoming = [next, scopes[(activeIndex + 2) % scopes.length]];

  return <Box className="rolodex-frame">
    <Box className="folder-navigation" role="tablist" aria-label="Dex views">
      <button className="folder-navigation-arrow" type="button" aria-label={`Go to ${previous.label}`} onClick={() => navigate(previous.id, 'previous')}>
        <ArrowBackIcon />
      </button>
      <span className={`folder-active-tab label-strip-${scope}`} role="tab" aria-selected="true">
        <span className="folder-label-text">{scopes[activeIndex].label.toUpperCase()}</span>
      </span>
      <button className="folder-navigation-arrow" type="button" aria-label={`Go to ${next.label}`} onClick={() => navigate(next.id, 'next')}>
        <ArrowForwardIcon />
      </button>
      <Box className="folder-upcoming-views" aria-label="Other Dex views">
        {upcoming.map((item, index) => <React.Fragment key={item.id}>
          {index > 0 && <span className="folder-upcoming-divider" aria-hidden="true">|</span>}
          <motion.span layout className={`folder-upcoming-view label-strip-${item.id}`} transition={{ layout: { duration: 0.34, ease: [0.22, 1, 0.36, 1] } }}>
            <span className="folder-label-text">{item.label.toUpperCase()}</span>
          </motion.span>
        </React.Fragment>)}
      </Box>
    </Box>
    <div className={`rolodex-card${transitionPhase !== 'open' ? ' is-transitioning' : ''}`}>
      <Folder3DScene phase={transitionPhase} direction={transitionDirection} />
      <Box className="rolodex-card-header">
        <IconButton aria-label={`Show ${previous.label}`} onClick={() => navigate(previous.id, 'previous')}><ArrowBackIcon /></IconButton>
        <span>{scopes[activeIndex].label}</span>
        <IconButton aria-label={`Show ${next.label}`} onClick={() => navigate(next.id, 'next')}><ArrowForwardIcon /></IconButton>
      </Box>
      <Box className={`rolodex-card-content scope-${scope}`}>{children}</Box>
    </div>
  </Box>;
}

export default RolodexFrame;
